import { LabelRelation, PrismicCollectionDetailField } from "types";
import { parseKeyValueLines } from "./key-value-lines";

export class LabelConfigurationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LabelConfigurationError";
  }
}

export function getSliceType(slice: any): string {
  return slice?.slice_type || String(slice?.key || "").split("$")[0];
}

export interface LabelValueMapping {
  baseValue: string;
  proposedValue: string;
}

export function parseLabelValueMapping(
  value: unknown,
  fieldName = "Label relation mapping"
): LabelValueMapping[] {
  let rows;
  try {
    rows = parseKeyValueLines(value, { fieldName, rejectInvalid: true });
  } catch (error) {
    throw new LabelConfigurationError(
      error instanceof Error ? error.message : String(error)
    );
  }
  if (rows.length === 0) {
    throw new LabelConfigurationError(`${fieldName} is empty.`);
  }

  const mappings = rows.map(({ key, value }) => ({
    baseValue: key,
    proposedValue: value,
  }));

  const proposalsByBase = new Map<string, string>();
  mappings.forEach(({ baseValue, proposedValue }) => {
    const existing = proposalsByBase.get(baseValue);
    if (existing && existing !== proposedValue) {
      throw new LabelConfigurationError(
        `${fieldName} maps ${baseValue} to more than one proposed key.`
      );
    }
    proposalsByBase.set(baseValue, proposedValue);
  });

  return Array.from(proposalsByBase, ([baseValue, proposedValue]) => ({
    baseValue,
    proposedValue,
  }));
}

export function getDropdownOptionKeys(value: unknown): string[] {
  return parseKeyValueLines(value)
    .map(({ key }) => key)
    .filter(Boolean);
}

export function getDropdownOptions(value: unknown): LabelValueMapping[] {
  return parseKeyValueLines(value)
    .filter(({ key, value }) => key && value)
    .map(({ key, value }) => ({ baseValue: key, proposedValue: value }));
}

export function getLabelRelations(
  slices: any[] | null | undefined
): LabelRelation[] {
  return (slices || [])
    .filter((slice) => getSliceType(slice) === "label_relation")
    .flatMap((slice) => slice.items || slice.value?.repeat || [])
    .flatMap((item, index) => {
      const baseLabelId = String(item.base_label_id || "").trim();
      const proposedLabelId = String(item.proposed_label_id || "").trim();
      if (!baseLabelId || !proposedLabelId) {
        throw new LabelConfigurationError(
          `Label relation ${index + 1} is missing a label ID.`
        );
      }

      const mappings = parseLabelValueMapping(
        item.value_mapping,
        `Label relation ${index + 1} mapping`
      );

      return mappings.map((mapping) => ({
        baseLabelId,
        baseValue: mapping.baseValue,
        proposedLabelId,
        proposedKey: mapping.proposedValue,
      }));
    });
}

export interface LabelProposalOption {
  key: string;
  value: string;
}

export interface LabelProposalTarget {
  id: string;
  allowedValues: string[];
  options: LabelProposalOption[];
  relatedLabelId?: string;
}

function targetRepresentation(
  target: LabelProposalTarget,
  configuredValue: string
): string | undefined {
  if (target.allowedValues.includes(configuredValue)) return configuredValue;
  const option = target.options.find(
    ({ key, value }) => key === configuredValue || value === configuredValue
  );
  if (!option) return undefined;
  if (target.allowedValues.includes(option.key)) return option.key;
  if (target.allowedValues.includes(option.value)) return option.value;
  return undefined;
}

function sameLabelRepresentation(
  target: LabelProposalTarget,
  knownValue: string
): string | undefined {
  if (target.allowedValues.includes(knownValue)) return knownValue;
  const option = target.options.find(
    ({ key, value }) => key === knownValue || value === knownValue
  );
  return option ? targetRepresentation(target, option.key) : undefined;
}

export function getCanonicalLabelValue(
  options: LabelProposalOption[],
  selectedValue: string
): string {
  const option = options.find(
    ({ key, value }) => key === selectedValue || value === selectedValue
  );
  return option?.key || selectedValue;
}

export function getLabelProposals({
  relations,
  knownValues,
  targets,
}: {
  relations: LabelRelation[];
  knownValues: Record<string, string>;
  targets: LabelProposalTarget[];
}): Record<string, string> {
  const proposals: Record<string, string> = {};

  for (const target of targets) {
    const candidates = new Set<string>();
    const knownValue = knownValues[target.id];
    if (knownValue) {
      const proposal = sameLabelRepresentation(target, knownValue);
      if (proposal) candidates.add(proposal);
    } else if (target.relatedLabelId) {
      const relatedTarget = targets.find(
        ({ id }) => id === target.relatedLabelId
      );
      const relatedKnownValue = knownValues[target.relatedLabelId];
      const relatedCanonicalValue = relatedTarget
        ? getCanonicalLabelValue(relatedTarget.options, relatedKnownValue)
        : relatedKnownValue;

      relations
        .filter(
          (relation) =>
            relation.baseLabelId === target.relatedLabelId &&
            relation.proposedLabelId === target.id &&
            relation.baseValue === relatedCanonicalValue
        )
        .forEach((relation) => {
          const proposal = targetRepresentation(target, relation.proposedKey);
          if (!proposal) {
            throw new LabelConfigurationError(
              `Label ${target.id} proposes a value that is not in its configured options.`
            );
          }
          candidates.add(proposal);
        });
    }

    const candidateValues = Array.from(candidates);
    const invalid = candidateValues.filter(
      (candidate) =>
        target.allowedValues.length > 0 &&
        !target.allowedValues.includes(candidate)
    );
    if (invalid.length > 0) {
      throw new LabelConfigurationError(
        `Label ${target.id} proposes a value that is not in its configured options.`
      );
    }
    if (candidates.size > 1) {
      throw new LabelConfigurationError(
        `Label ${target.id} has conflicting configured proposals.`
      );
    }

    const [proposal] = candidateValues;
    if (proposal) proposals[target.id] = proposal;
  }

  return proposals;
}

export function getStepDropdownValues(
  field: PrismicCollectionDetailField
): string[] {
  if (field.label_type !== "dropdown" || !field.dropdown_options) return [];
  return getDropdownOptionKeys(field.dropdown_options);
}
